// External Dependencies
import { useEffect, useMemo, useState } from 'react';
import usaMap from '@highcharts/map-collection/countries/us/us-all.geo.json';
import { lighten } from '@mui/material/styles';
import { useTheme } from 'styled-components';

// Internal Dependencies
import { useGetAllOrganizations } from 'hooks/api/organizations';
import { useGetStateOptions } from 'hooks/useGetStateOptions';

// Local Typings
export interface FipsData {
  fips: string;
  name: string;
}

export const useChartState = ({
  selectedFips,
  selectedState,
  setSelectedFips,
  setSelectedState,
}: {
  selectedFips: FipsData | null;
  selectedState: string | null;
  setSelectedFips: (fips: FipsData | null) => void;
  setSelectedState: (state: string | null) => void;
}) => {
  const [stateMap, setStateMap] = useState<any>(null);
  const [isLoadingStateMap, setIsLoadingStateMap] = useState<boolean>(false);

  const {
    data: organizationsResponse,
    isLoading,
  } = useGetAllOrganizations();

  const theme = useTheme();

  const stateOptions = useGetStateOptions();

  // Helper function to get state name from state code
  const getStateNameFromCode = (stateCode: string): string => {
    const stateAbbr = stateCode.split('-')[1]?.toUpperCase();
    if (!stateAbbr || !stateOptions?.options) return stateCode;

    const stateOption = stateOptions.options.find(
      (option) => option.abbreviation.toLowerCase() === stateAbbr.toLowerCase(),
    );

    return stateOption?.label || stateAbbr;
  };

  // Filter organizations by selected FIPS code
  const organizationsForSelectedFips = useMemo(() => {
    if (!selectedFips || !organizationsResponse?.data.data) return [];

    return organizationsResponse.data.data.filter(
      (org) => org.hasActiveLicense && org.fips === selectedFips?.fips,
    );
  }, [selectedFips, organizationsResponse]);

  // Load state map when a state is selected
  useEffect(() => {
    if (selectedState) {
      setIsLoadingStateMap(true);

      // Extract state abbreviation from the selectedState (format: us-xx)
      const stateAbbr = selectedState.split('-')[1]?.toUpperCase();

      if (stateAbbr) {
        // Find the state option to get the full name
        const stateOption = stateOptions?.options?.find(
          (option) => option.abbreviation.toLowerCase() === stateAbbr.toLowerCase(),
        );

        if (stateOption) {
          // Dynamically import the state map
          import(`@highcharts/map-collection/countries/us/us-${stateAbbr.toLowerCase()}-all.geo.json`)
            .then((module) => {
              setStateMap(module.default);
              setIsLoadingStateMap(false);
            })
            .catch((error) => {
              console.error(`Failed to load map for state ${stateAbbr}:`, error);
              setIsLoadingStateMap(false);
              setSelectedState(null);
            });
        }
      }
    } else {
      setStateMap(null);
    }
  }, [selectedState, stateOptions]);

  // Format data for Highcharts
  const chartData = useMemo(() => {
    if (!organizationsResponse?.data.data || !stateOptions?.options) return [];

    // If a state is selected, show zipcode data for that state
    if (selectedState && stateMap) {
      // Extract state abbreviation from the selectedState (format: us-xx)
      const stateAbbr = selectedState.split('-')[1]?.toUpperCase();

      if (!stateAbbr) return [];

      // Find the state option to get the state ID
      const stateOption = stateOptions.options.find(
        (option) => option.abbreviation.toLowerCase() === stateAbbr.toLowerCase(),
      );

      if (!stateOption) return [];

      // Count organizations by zipcode for the selected state
      const fipsCount: Record<string, number> = {};
      const fipsMap: Record<string, string> = {};

      // Filter organizations by the selected state
      const stateOrgs = organizationsResponse.data.data.filter(
        (org) => org.hasActiveLicense && org.stateId === stateOption.id,
      );

      // Count organizations by zipcode
      stateOrgs.forEach((org) => {
        if (org.fips) {
          fipsCount[org.fips] = (fipsCount[org.fips] || 0) + 1;
          fipsMap[org.fips] = org.fips;
        }
      });

      // Get all counties/regions from the state map
      const mapRegions = stateMap.features.map((feature: any) => ({
        fips: feature.properties.fips,
        'hc-key': feature.properties['hc-key'],
        name: feature.properties.name,
      }));

      return mapRegions.map((region: any) => {
        const matchingFips = Object.keys(fipsCount).filter(
          (fips) => region.fips === fips,
        );

        let value = 0;

        matchingFips.forEach((fips) => {
          value += fipsCount[fips] || 0;
        });

        return {
          fips: matchingFips[0] || null,
          'hc-key': region['hc-key'],
          name: region.name,
          value: value || null,
        };
      });
    }
    // Show state-level data for the US map
    const stateCount: Record<string, number | null> = {};
    const stateCodeMap: Record<string, string> = {};

    stateOptions.options.forEach((option) => {
      const stateCode = `us-${option.abbreviation.toLowerCase()}`;
      stateCount[stateCode] = null; // Initialize count for each state
      stateCodeMap[stateCode] = option.abbreviation; // Map state code to abbreviation
    });

    organizationsResponse.data.data.forEach((org) => {
      const { hasActiveLicense, stateId } = org;

      if (!hasActiveLicense) {
        return; // Skip organizations without an active license
      }

      if (stateId) {
        const stateOption = stateOptions.options.find((option) => option.id === stateId);

        if (stateOption) {
          const stateCode = `us-${stateOption.abbreviation.toLowerCase()}`;

          stateCount[stateCode] = (stateCount[stateCode] || 0) + 1;
          stateCodeMap[stateCode] = stateOption.abbreviation;
        }
      }
    });

    // Format data for Highcharts
    return Object.entries(stateCount).map(([stateCode, count]) => ({
      'hc-key': stateCode,
      name: stateCodeMap[stateCode] || stateCode,
      stateCode,
      value: count,
    }));
  }, [organizationsResponse, stateOptions, selectedState, stateMap]);

  // Highcharts options
  const chartOptions = useMemo(() => {
    return {
      chart: {
        height: window.innerHeight * 0.75,
        map: selectedState && stateMap ? stateMap : usaMap,
      },
      colorAxis: {
        maxColor: theme.palette.primary.dark,
        min: 1,
        minColor: lighten(theme.palette.primary.light, 0.5),
        type: 'logarithmic',
      },
      credits: {
        enabled: false,
      },
      legend: {
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      mapNavigation: {
        buttonOptions: {
          verticalAlign: 'bottom',
        },
        enabled: true,
      },
      series: [{
        data: chartData,
        dataLabels: {
          enabled: true,
          format: '{point.name}',
        },
        events: {
          click(e: any) {
            if (e.point && !selectedState) {
              // Only allow clicking on states when viewing the US map
              const { stateCode } = e.point;
              setSelectedState(stateCode);
            }

            if (e.point && selectedState) {
              setSelectedFips({ fips: e.point.options.fips, name: e.point.options.name });

              // If a state is selected, clicking on a region should not change the state
              e.preventDefault();
            }
          },
        },
        name: selectedState ? 'Organizations by Region' : 'Organizations by State',
        states: {
          hover: {
            color: theme.palette.primary.dark,
          },
        },
      }],
      title: {
        text: selectedState
          ? `Licensed organizations in ${getStateNameFromCode(selectedState)}`
          : 'Licensed organizations in the US',
      },
      tooltip: {
        headerFormat: '',
        pointFormat: selectedState
          ? '{point.name}: <b>{point.value}</b> organizations'
          : '{point.name}: <b>{point.value}</b> organizations',
      },
    };
  }, [chartData, theme, selectedState, stateMap]);

  return useMemo(() => ({
    chartData,
    chartOptions,
    isLoading,
    isLoadingStateMap,
    organizationsForSelectedFips,
  }), [
    chartData,
    chartOptions,
    isLoading,
    isLoadingStateMap,
    organizationsForSelectedFips,
  ]);
};
